import type { Catalog, Song, SeasonKey } from "../types";
import { suggestSongs } from "./songs";

// One option in the song autocomplete. `song` is set when the title is in the
// curated catalog (so we can default the annotation / show "déjà chanté"); titles
// that only exist in the big lyrics library carry just a title.
export interface SongOption {
  title: string;
  song?: Song;
  reason?: string; // shown for the empty-input moment suggestions
  inRepertoire?: boolean;
  usedCount?: number;
  carnetPage?: string; // page in the parish carnet de chant, when this song is in it
  carnetRef?: string; // its SECLI cote (e.g. "D 366")
  carnetCat?: string; // its thematic section in the carnet (e.g. "Vierge Marie")
}

// The parish carnet de chant (songbook), keyed by normalised title → page/cote.
export interface CarnetEntry { title: string; page: string; ref?: string; cat?: string }
export type CarnetIndex = Map<string, CarnetEntry>;

export const norm = (s: string) =>
  (s || "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").replace(/[^a-z0-9]+/g, " ").trim();
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// ---- the lyrics-library titles index (public/data/song-titles.json), cached ----
let cache: string[] | null = null;
let inflight: Promise<string[]> | null = null;
export function loadSongTitles(): Promise<string[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch("/data/song-titles.json", { cache: "force-cache" })
      .then((r) => r.json())
      .then((d) => (cache = Array.isArray(d?.titles) ? d.titles : []))
      .catch(() => (cache = []));
  }
  return inflight;
}

/**
 * Songs matching `query`, across the curated catalog AND the lyrics library.
 * Empty query → the top moment/season suggestions (so focusing browses them).
 */
export function matchSongs(
  query: string,
  catalog: Catalog,
  extraTitles: string[],
  moment: string,
  season: SeasonKey,
  limit = 10,
  carnet: CarnetIndex | null = null
): SongOption[] {
  const q = norm(query);
  if (!q) {
    const base = suggestSongs(catalog, moment, season)
      .filter((r) => r.score > 1)
      .slice(0, 8)
      .map((r): SongOption => ({ title: r.song.title, song: r.song, reason: r.reasons[0], inRepertoire: r.song.sources.includes("notion"), usedCount: r.song.usedCount }));
    return withCarnet(base, carnet);
  }

  // Build the pool once: catalog (rich) wins over a duplicate lyrics-only title,
  // and carnet-only titles join so the parish songbook is searchable too.
  const byNorm = new Map<string, SongOption>();
  for (const s of catalog.songs) {
    byNorm.set(norm(s.title), { title: s.title, song: s, usedCount: s.usedCount, inRepertoire: s.sources.includes("notion") });
  }
  for (const t of extraTitles) {
    const k = norm(t);
    if (k && !byNorm.has(k)) byNorm.set(k, { title: t });
  }
  if (carnet) for (const [k, c] of carnet) { if (!byNorm.has(k)) byNorm.set(k, { title: c.title }); }

  const wordRe = new RegExp("\\b" + escapeRe(q));
  const scored: { opt: SongOption; score: number }[] = [];
  for (const [nt, opt] of byNorm) {
    let s = -1;
    if (nt === q) s = 1000;
    else if (nt.startsWith(q)) s = 800;
    else if (wordRe.test(nt)) s = 600;
    else if (nt.includes(q)) s = 400;
    else if (opt.song) {
      for (const a of opt.song.aliases) {
        const na = norm(a);
        if (na.includes(q)) s = Math.max(s, na.startsWith(q) ? 500 : 300);
      }
    }
    if (s < 0) continue;
    if (opt.song) s += 40 + Math.min(opt.usedCount || 0, 5) * 4 + (opt.song.moments.includes(moment) ? 25 : 0) + (opt.song.seasons.includes(season) ? 10 : 0);
    if (carnet?.has(nt)) s += 30; // nudge songs the parish actually has in its carnet
    scored.push({ opt, score: s });
  }
  scored.sort((a, b) => b.score - a.score || a.opt.title.localeCompare(b.opt.title, "fr"));
  return withCarnet(scored.slice(0, limit).map((x) => x.opt), carnet);
}

// Stamp each option with its carnet page/cote when the parish has it in the songbook.
function withCarnet(opts: SongOption[], carnet: CarnetIndex | null): SongOption[] {
  if (!carnet) return opts;
  for (const o of opts) {
    const c = carnet.get(norm(o.title));
    if (c) { o.carnetPage = c.page; o.carnetRef = c.ref; o.carnetCat = c.cat; }
  }
  return opts;
}
